
import img0 from './Images/SAML_New_Application.png';
import img1 from './Images/SAML_Configuration.png';
import img2 from './Images/SAML_Configuration_Step1.png';
import img3 from './Images/SAML_Configuration_Step2.png';
import img4 from './Images/App_Role_Configuration.png';
import img5 from './Images/SAML_OnData_Configuration.png';
  
const content = `# Microsoft Entra ID Integration Using SAML

OnData supports the integration with Identity and Access Management (IAM) systems for user authentication and authorization, based on the industry standard 
protocols, including SAML 2.0 and OpenID Connect (OIDC) protocols. The purpose of this document is to detail the process of configuring OnData and Microsoft Entra ID 
(formerly Azure Active Directory) using the SAML 2.0 protocol, so that the users can login to OnData with their Microsoft Entra ID accounts.

**Note**: When choosing between the SAML 2.0 protocol and the OpenID Connect protocol, please consider whether you would like to enable the "SecureFile File Sharing" 
feature later on. The "SecureFile File Sharing" feature allows users to securely share sensitive documents with both external and internal users leveraging the 
Digital Rights Management (DRM) technology supported by Microsoft Azure Information Protection Service. The "SecureFile File Sharing" feature requires: 1) the OIDC 
protocol is used for Entra ID integration; and 2) the Microsoft Azure Information Protection Service is available with your Office 365 subscription. Please choose 
the OIDC protocol and follow the instructions in [**Microsoft Entra ID Integration Using OIDC**](reference_doc#ondata_portal/sso_integration/entraid_integration_using_oidc) if you would like to enable the 
"SecureFile File Sharing" feature later on.  
  
### 1. Prerequisite
a. An Entra ID user account with at least one of the following roles: Global Administrator, Cloud Application Administrator, or Application Administrator  
b. An OnData account with the System Administrator role  

### 2. Entra ID Configuration
a. Login to the Azure Portal, and select Microsoft Entra ID  
b. Select "Manage" -> “Enterprise applications” from the left menu  
c. Select “New application” from the top menu  

![Create New Application](${ img0 })

d. Select “Create your own application” from the top menu  
e. Use “OnData Portal” as the name of the application. Use the default option of “Integrate any other application you don’t find in the gallery (Non-gallery)”. 
Then click on the “Create” button to create the application  
f. After the application is created, on the application overview page, select “Get started” under the tile “2. Set up single sign on”  
g. Select “SAML” as the single sign-on method  
h. The “Set up Single Sign-On with SAML” page will be displayed, as illustrated below  

![SAML Configuration](${ img1 })

i. Click on “Edit” on Step 1 – “Basic SAML Configuration”  
j. Click on “Add identifier” and enter the value of “users.ondatahub.com”  
k. Click on “Add reply URL” and copy the "SAML Assertion Consumer Service URL" from the OnData Portal to complete the "Reply URL" field. Below are the steps 
to find the "SAML Assertion Consumer Service URL":  
  * Login to the OnData Portal as a System Administrator, select the "Settings" icon from the top right corner, and select "Organization" from the dropdown menu  
  * Select the "SSO Configuration" tab  
  * Toggle the switch to "Enable Single Sign-ON (SSO)"  
  * Under the "Protocol" choice, select the "SAML 2.0" option and copy the "SAML Assertion Consumer Service URL". And it should 
  be: https://api.ondatahub.com/users/api/sso/oidc/consumer  

l. Leave the other optional fields empty, click on the “Save” button, and close the “Basic SAML Configuration” pop-up

![SAML Configuration Step 1](${ img2 })

m. Click on “Edit” on Step 2 – “Attributes & Claims”  
n. Click on “Add new claim” at the top menu, use “email” as the “Name” of the attribute, use the default value of “Attribute” for the “Source” field, 
and use “user.mail” for the “Source attribute” field. Leave all other fields with default values. Click on “Save” to save this attribute, and close the 
“Manage claim” pop-up  

![SAML Configuration Step 2](${ img3 })

o. Similarly, click on “Add new claim” to add another claim with the name of "roles", use the default value of “Attribute” for the “Source” field, and select 
value of "user.assignedroles" for the “Source attribute” field. Leave all other fields with default values. Click on “Save” to save this attribute, and close the 
“Manage claim” pop-up and the “Attributes & Claims” pop-up  
p. Under Step 3 – “SAML Certificates”, download the “Certificate (Base64)” file. This certificate file is needed to configure the 
“Identity Provider X.509 Certificate” field at the OnData side  
q. Under Step 4 – “Set up OnData Portal”:  
- The value of the “Login URL” is needed to configure the “Sign In URL” field at the OnData side  
- The value of the “Microsoft Entra Identifier” is needed to configure the “Service Provider Entity ID” field at the OnData side  
- The value of the “Logout URL” is needed to configure the “On Logout, Sign Out IdP at URL” field at the OnData side
    
r. Go back to the Microsoft Entra ID home page, and select "Manage" -> "App registrations"  
s. Find the newly created "OnData Portal" application, and select "Manage" -> "App roles" from the left menu  
t. Click on "Create app role" to create the OnData "System Administrator" app role  
u. Use "System Administrator" as the "Display name", and select the "Users/Groups" option under the "Allowed member types". Enter the role value of 
"SystemAdministrator", which should match the role value of the System Administrator role at the OnData Portal. Enter a description and select 
the "Apply" button to create the role  
  
![App Role Configuration](${ img4 })  
  
v. You may follow the same process to create all other app roles that match what you have at the OnData Portal, including the custom roles you have created. 
But you may choose to do that later  
w. Go back to the Microsoft Entra ID home page, select "Manage" -> “Enterprise applications” on the left menu  
x. Select the “OnData Portal” application, and on the “Overview” page, select “Assign users and groups” under the tile named “1.  Assign users and groups”  
y. Click on "Add user/group" to add one or more System Administrator users. Select one or more users from Entra ID, and select the System Administrator role, 
then click on the "Assign" button to complete the assignment  
  
### 3. OnData Configuration
a. Login to the OnData Portal as a System Administrator, select the Settings icon from the top right corner, and select Organization from the dropdown menu  
b. Select the SSO Configuration tab  
c. Toggle the switch to “Enable Single Sign-ON (SSO)”  
d. Under the Protocol choice, select the “SAML 2.0” option  
e. Fill out the “Service Provider Entity ID” field with the value of the “Microsoft Entra Identifier” field from Azure Portal  
f. Fill out the “Sign In URL” field with the value of the “Login URL” field from Azure Portal  
g. Upload the “Certificate (Base64)” file downloaded from Entra ID to the “Identity Provider X.509 Certificate” field  
h. Fill out the “On Logout, Sign Out IdP at URL” field with the value of the “Logout URL” field from Entra ID, as illustrated below  
i. Click on the “Update SSO Configuration” button to update the SSO settings  
j. Log out of OnData and login again with a user’s organization email address who has been granted access to the OnData Portal application in 
Step 2 – Entra ID Configuration above  
k. You should be redirected to the Entra ID for login. After a successful login, you will be redirected back to the OnData Portal  

![SAML OnData Configuration](${ img5 })
`
  
export default content;
